import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

const ShopPreviewSection = ({ profileId, vendorId, accentColor }) => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingProductId, setProcessingProductId] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = useRef(null);

  // Helper to get included items for Lichess courses
  const getLichessIncludedItems = (product) => {
    const items = [];

    // Check if Lichess Study access is included (default true if not specified)
    if (product.lichessStudyEnabled !== false) {
      items.push({ type: 'study', icon: 'fas fa-chess-board', label: 'Lichess Study', bgColor: 'bg-black/90' });
    }

    // Check if videos are included
    if (product.youtubePlaylistUrl || (product.youtubeUrls && product.youtubeUrls.length > 0)) {
      items.push({ type: 'video', icon: 'fab fa-youtube', label: 'Videos', bgColor: 'bg-red-600/90' });
    }

    // Check if PDF is included
    if (product.pdfEnabled) {
      items.push({ type: 'pdf', icon: 'fas fa-file-pdf', label: 'eBook PDF', bgColor: 'bg-purple-600/90' });
    }

    return items;
  };

  // Helper to find Black mockup (or first if no Black)
  const findBlackMockup = (product) => {
    // For POD products, try to find Black variant mockup
    if (product.product_type === 'pod' && product.variants?.length > 0) {
      const firstVariant = product.variants[0];
      if (!firstVariant.mockup_images || !firstVariant.variant_map) {
        return product.preview_image || firstVariant.mockup_images_urls?.[0] || null;
      }

      // Find any Black variant key (black_s, black_m, black_xl, etc.)
      const blackVariantKey = Object.keys(firstVariant.variant_map).find(key =>
        key.startsWith('black_')
      );

      if (blackVariantKey) {
        const blackPrintifyVariantId = firstVariant.variant_map[blackVariantKey];
        const blackMockup = firstVariant.mockup_images.find(img =>
          img.variant_ids && img.variant_ids.includes(blackPrintifyVariantId)
        );
        if (blackMockup?.src) {
          return blackMockup.src;
        }
      }

      // Fallback to stored preview or first mockup
      return product.preview_image || firstVariant.mockup_images_urls?.[0] || null;
    }

    // For non-POD or products without variants, use existing image
    return product.preview_image || product.imageUrl || null;
  };

  // Load products
  useEffect(() => {
    const loadProducts = async () => {
      if (!vendorId) {
        setLoading(false);
        return;
      }

      try {
        const q = query(
          collection(db, 'products'),
          where('vendorId', '==', vendorId),
          where('isActive', '==', true)
        );

        const snapshot = await getDocs(q);
        const productsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setProducts(productsData);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [vendorId]);

  // Carousel navigation functions
  const scrollToProduct = (index) => {
    if (isTransitioning || !carouselRef.current) return;

    const container = carouselRef.current;
    const cardWidth = 350; // Fixed card width
    const gap = 16; // 1rem gap
    const scrollPosition = index * (cardWidth + gap);

    setIsTransitioning(true);
    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });

    setCurrentIndex(index);
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const nextProduct = () => {
    const nextIndex = Math.min(currentIndex + 1, products.length - 1);
    scrollToProduct(nextIndex);
  };

  const prevProduct = () => {
    const prevIndex = Math.max(currentIndex - 1, 0);
    scrollToProduct(prevIndex);
  };

  // Handle details click
  const handleDetailsClick = (productId) => {
    navigate(`/product/${profileId}/${productId}`);
  };

  // Handle buy click
  const handleBuyClick = async (productId) => {
    setProcessingProductId(productId);

    try {
      const response = await fetch(`https://us-central1-chess-web-host.cloudfunctions.net/createShopCheckoutSession`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          productId,
          origin: window.location.origin,
          cancelReturnTo: window.location.pathname
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle error responses
        console.error('Checkout failed:', {
          status: response.status,
          error: data.error,
          details: data.details
        });

        const errorMessage = data.details || data.error || 'Failed to start checkout. Please try again.';
        alert(errorMessage);
        setProcessingProductId(null);
        return;
      }

      if (data.sessionUrl) {
        // Redirect to Stripe Checkout
        window.location.href = data.sessionUrl;
      } else {
        alert('Failed to start checkout. Please try again.');
        setProcessingProductId(null);
      }

    } catch (error) {
      console.error('Error starting checkout:', error);
      alert('Network error: Unable to connect to checkout service. Please try again.');
      setProcessingProductId(null);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chess-green"></div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <i className="fas fa-inbox text-4xl mb-3"></i>
        <p>No products available yet</p>
      </div>
    );
  }

  return (
    <div className="relative max-w-2xl mx-auto">
      {/* Navigation - Previous */}
      {products.length > 1 && currentIndex > 0 && (
        <button
          onClick={prevProduct}
          disabled={isTransitioning}
          className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-gray-800 hover:bg-gray-700 text-white rounded-full w-12 h-12 flex items-center justify-center transition-all shadow-lg disabled:opacity-50"
          style={{ borderColor: accentColor || '#4ade80', borderWidth: '2px' }}
        >
          <i className="fas fa-chevron-left text-xl"></i>
        </button>
      )}

      {/* Carousel Container */}
      <div
        ref={carouselRef}
        className="overflow-x-auto snap-x snap-mandatory"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <div className={`flex gap-4 pb-2 ${products.length <= 2 ? 'justify-center' : ''}`}>
          {products.map(product => {
            const isLichess = product.type === 'lichess-course';

            return (
            <div
              key={product.id}
              className="flex-none w-[350px] rounded-lg shadow-md hover:shadow-xl transition-all border-2 overflow-hidden bg-gray-800 snap-start relative"
              style={{ borderColor: accentColor || '#4ade80' }}
            >
              {/* Product Image - SQUARE ENFORCED - use Black mockup */}
              {findBlackMockup(product) && (
                <div className="w-full aspect-square overflow-hidden bg-gray-900 relative">
                  <img
                    src={findBlackMockup(product)}
                    alt={product.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      console.error('Image failed to load:', findBlackMockup(product));
                      e.target.style.display = 'none';
                    }}
                  />

                  {/* Lichess Course - Show included items */}
                  {isLichess && (() => {
                    const includedItems = getLichessIncludedItems(product);
                    return (
                      <div className="absolute top-2 left-2 right-2 flex flex-wrap gap-1 justify-end">
                        {includedItems.map((item, idx) => (
                          <div
                            key={idx}
                            className={`${item.bgColor} backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full flex items-center gap-1 shadow-lg`}
                          >
                            <i className={item.icon}></i>
                            <span>{item.label}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* POD Badge */}
                  {product.product_type === 'pod' && !isLichess && (
                    <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1 shadow-lg">
                      <i className="fas fa-shipping-fast"></i>
                      <span>Ships in 3-7 days</span>
                    </div>
                  )}

                  {/* Digital Product Badge */}
                  {product.product_type !== 'pod' && !isLichess && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-3 py-1 rounded-full flex items-center gap-1 shadow-lg">
                      <i className="fas fa-bolt"></i>
                      <span>Delivers instantly</span>
                    </div>
                  )}
                </div>
              )}

              {/* Card Content - Compact */}
              <div className="p-3 space-y-2">
                {/* Title */}
                <div>
                  <h3 className="text-lg font-bold text-white line-clamp-2">
                    {product.title}
                  </h3>
                </div>

                {/* Description - Compact */}
                <p className="text-gray-300 text-xs line-clamp-2">
                  {product.description || 'No description available'}
                </p>

                {/* Footer - Price & Buttons */}
                <div className="pt-2 border-t border-gray-700 space-y-2">
                  <div className="flex items-center justify-between">
                    <span
                      className="text-xl font-bold"
                      style={{ color: accentColor || '#4ade80' }}
                    >
                      {product.price_min ? (
                        `$${(product.price_min / 100).toFixed(2)}+`
                      ) : (
                        `$${(product.price / 100).toFixed(2)}`
                      )}
                    </span>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDetailsClick(product.id)}
                      className="flex-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all bg-gray-700 hover:bg-gray-600 text-white"
                    >
                      <i className="fas fa-info-circle mr-1"></i>
                      Details
                    </button>

                    <button
                      onClick={() => handleDetailsClick(product.id)}
                      className="flex-1 px-3 py-1.5 rounded-lg text-sm font-semibold transition-all shadow-lg hover:opacity-90"
                      style={{
                        backgroundColor: accentColor || '#4ade80',
                        color: 'white'
                      }}
                    >
                      <i className="fas fa-shopping-cart mr-1"></i>
                      Buy Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
          })}
        </div>
      </div>

      {/* Navigation - Next */}
      {products.length > 1 && currentIndex < products.length - 1 && (
        <button
          onClick={nextProduct}
          disabled={isTransitioning}
          className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-gray-800 hover:bg-gray-700 text-white rounded-full w-12 h-12 flex items-center justify-center transition-all shadow-lg disabled:opacity-50"
          style={{ borderColor: accentColor || '#4ade80', borderWidth: '2px' }}
        >
          <i className="fas fa-chevron-right text-xl"></i>
        </button>
      )}

     

      {/* Global style for hiding scrollbar on carousel */}
      <style>{`
        .overflow-x-auto.snap-x::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ShopPreviewSection;
